import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {Employee, Team} from 'client';
import {$createMentionNode} from 'components/DSYS/editor/nodes/MentionNode';
import {useTeamsMap} from 'components/teams/hooks';
import {useEmployeesBatch} from 'hooks/useEmployee';
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  PASTE_COMMAND,
  PasteCommandType,
} from 'lexical';
import {useCallback, useEffect, useRef, useState} from 'react';

// Regex for both absolute and relative URLs
const MENTION_LINK_REGEX =
  /(?:https?:\/\/(?:localhost:3000|.*amplifyapp\.com|os\.dropbox\.com|dropboxpulse\.com))?\/?(people|teams)\/([^/\s]+)/i;

// Create a mention node from a match
const createMentionNodeFromMatch = (
  match: RegExpMatchArray,
  teamData?: Team,
  employeeData?: Employee
) => {
  try {
    const [, type, slug] = match;
    const mentionType = type === 'teams' ? 'team' : 'employee';

    if (type === 'teams' && teamData) {
      return $createMentionNode(teamData.name || slug, slug, 'team', teamData.team_id || slug);
    } else if (type === 'people' && employeeData) {
      return $createMentionNode(
        employeeData.name || slug,
        employeeData.ldap || slug,
        'employee',
        employeeData.user_id || slug
      );
    } else {
      // Fallback if data is not available
      return $createMentionNode(slug, slug, mentionType, slug);
    }
  } catch {
    return null;
  }
};

export const AutoLinkCompanyOSPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const processingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [mentionsToProcess, setMentionsToProcess] = useState<Array<{type: string; slug: string}>>(
    []
  );
  const [selectionToUse, setSelectionToUse] = useState<any>(null);
  const processedMentionsRef = useRef<Set<string>>(new Set());

  // Get unique slugs for teams and employees
  const teamSlugs = Array.from(
    new Set(mentionsToProcess.filter((m) => m.type === 'teams').map((m) => m.slug))
  );
  const employeeLdaps = Array.from(
    new Set(mentionsToProcess.filter((m) => m.type === 'people').map((m) => m.slug))
  );

  // Fetch teams and employees data
  const {teamsMap = {} as Record<string, Team>, isLoading: isTeamsLoading} = useTeamsMap({
    slugs: teamSlugs,
  });
  const {employees: employeesList, isLoading: isEmployeesLoading} = useEmployeesBatch({
    ldaps: employeeLdaps,
    enabled: employeeLdaps.length > 0,
  });

  // Create maps for quick lookup
  const employeesMap = (employeesList || []).reduce(
    (acc, emp) => {
      if (emp.ldap) {
        return {...acc, [emp.ldap]: emp};
      }
      return acc;
    },
    {} as Record<string, Employee>
  );

  // Insert mention nodes into the editor
  const insertMentionNodes = useCallback(
    (selection: any, mentions: Array<{type: string; slug: string}>) => {
      if (!selection) return;

      try {
        const nodes = [];

        for (const mention of mentions) {
          let mentionNode;
          if (mention.type === 'teams') {
            const teamData = (teamsMap as any)[mention.slug];
            mentionNode = createMentionNodeFromMatch(
              ['', 'teams', mention.slug],
              teamData,
              undefined
            );
          } else if (mention.type === 'people') {
            const employeeData = employeesMap[mention.slug];
            mentionNode = createMentionNodeFromMatch(
              ['', 'people', mention.slug],
              undefined,
              employeeData
            );
          } else {
            mentionNode = createMentionNodeFromMatch(
              ['', mention.type, mention.slug],
              undefined,
              undefined
            );
          }

          if (mentionNode) {
            nodes.push(mentionNode);
            nodes.push($createTextNode(' '));
          }
        }

        if (nodes.length > 0) {
          selection.insertNodes(nodes);
        }
      } catch {
        // Error handling
      }
    },
    [employeesMap, teamsMap]
  );
  // Process mentions when data is loaded
  useEffect(() => {
    if (mentionsToProcess.length > 0 && selectionToUse && !processingRef.current) {
      const isDataLoading = isTeamsLoading || (employeeLdaps.length > 0 && isEmployeesLoading);

      if (!isDataLoading) {
        // Create a unique key for these mentions
        const mentionsKey = mentionsToProcess.map((m) => `${m.type}:${m.slug}`).join(',');

        // Skip if already processed
        if (processedMentionsRef.current.has(mentionsKey)) {
          setMentionsToProcess([]);
          setSelectionToUse(null);
          return;
        }

        // Mark as processed
        processedMentionsRef.current.add(mentionsKey);

        // Insert the nodes
        editor.update(() => {
          insertMentionNodes(selectionToUse, mentionsToProcess);
        });

        // Reset state
        setMentionsToProcess([]);
        setSelectionToUse(null);
      }
    }
  }, [
    editor,
    employeeLdaps.length,
    insertMentionNodes,
    teamsMap,
    employeesMap,
    isEmployeesLoading,
    isTeamsLoading,
    mentionsToProcess,
    selectionToUse,
  ]);

  // Process paste content
  const processPasteContent = (clipboardData: any, selection: any) => {
    if (processingRef.current) return false;

    processingRef.current = true;

    // Set timeout to prevent hanging
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      processingRef.current = false;
      setMentionsToProcess([]);
      setSelectionToUse(null);
    }, 5000);

    try {
      const html = clipboardData.getData('text/html');
      if (html) {
        const tempDiv = document.createElement('div');
        const links = tempDiv.querySelectorAll('a');

        if (links.length > 0) {
          let foundMention = false;
          const mentionsToFetch: Array<{type: string; slug: string}> = [];

          // Limit mentions to prevent hanging
          const maxMentions = 10;
          let processedCount = 0;

          for (const link of Array.from(links)) {
            if (processedCount >= maxMentions) break;

            const href = link.getAttribute('href');
            const textContent = link.textContent || '';

            if (href) {
              const match = href.match(MENTION_LINK_REGEX);

              if (match) {
                foundMention = true;
                const [, type, slug] = match;
                mentionsToFetch.push({type, slug});
                processedCount++;
              }
              // Check for @ mentions
              else if (textContent.startsWith('@')) {
                const name = textContent.substring(1);
                const type = href.includes('/teams/') ? 'teams' : 'people';
                const slug = name;

                foundMention = true;
                mentionsToFetch.push({type, slug});
                processedCount++;
              }
            }
          }

          if (foundMention) {
            setMentionsToProcess(mentionsToFetch);
            setSelectionToUse(selection.clone());
            processingRef.current = false;
            return true;
          }
        }
      }

      const text = clipboardData.getData('text/plain');
      if (!text) {
        processingRef.current = false;
        return false;
      }

      // Check for mentions in plain text
      const matches = [...text.matchAll(MENTION_LINK_REGEX)];

      if (matches.length > 0) {
        const mentionsToFetch: Array<{type: string; slug: string}> = [];

        const maxMentions = 10;
        const limitedMatches = matches.slice(0, maxMentions);

        for (const match of limitedMatches) {
          const [, type, slug] = match;
          mentionsToFetch.push({type, slug});
        }

        setMentionsToProcess(mentionsToFetch);
        setSelectionToUse(selection.clone());
        processingRef.current = false;
        return true;
      }

      processingRef.current = false;
      return false;
    } catch {
      processingRef.current = false;
      return false;
    }
  };

  // Register paste command handler
  useEffect(() => {
    const removePasteListener = editor.registerCommand(
      PASTE_COMMAND,
      (event: PasteCommandType) => {
        try {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return false;
          }

          const clipboardData = (event as any).clipboardData;
          if (!clipboardData) {
            return false;
          }

          return processPasteContent(clipboardData, selection);
        } catch {
          return false;
        }
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      removePasteListener();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [editor]);

  return null;
};
